import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Chip,
  Grid,
  MenuItem,
  IconButton,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { API } from '../services/api';
import { showSuccess, showError } from '../utils/notifications';
import DataTable from '../components/DataTable';
import FilterBar from '../components/FilterBar';
import ConfirmDialog from '../components/ConfirmDialog';
import ActionButton from '../components/ActionButton';
import TableSkeleton from '../components/TableSkeleton';

const MotionBox = motion(Box);

function APIKeys() {
  const [apiKeys, setApiKeys] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, keyId: null });
  const [editingKey, setEditingKey] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [showKey, setShowKey] = useState({});
  const [formData, setFormData] = useState({
    keyname: '',
    project_id: '',
    expiresat: '',
    status: 1,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [keysRes, projectsRes] = await Promise.all([
        API.apiKeys.list(),
        API.projects.list(),
      ]);
      setApiKeys(keysRes?.data || []);
      setProjects(projectsRes?.data || []);
    } catch (err) {
      showError('Failed to load API keys');
      console.error('API keys fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = (key = null) => {
    if (key) {
      setEditingKey(key);
      setFormData({
        keyname: key.keyname || '',
        project_id: key.project_id || '',
        expiresat: key.expiresat ? key.expiresat.split('T')[0] : '',
        status: key.status !== undefined ? key.status : 1,
      });
    } else {
      setEditingKey(null);
      setFormData({
        keyname: '',
        project_id: '',
        expiresat: '',
        status: 1,
      });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingKey(null);
    setFormData({
      keyname: '',
      project_id: '',
      expiresat: '',
      status: 1,
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingKey) {
        await API.apiKeys.update(editingKey.apikeyid, formData);
        showSuccess('API key updated successfully!');
      } else {
        await API.apiKeys.create(formData);
        showSuccess('API key created successfully!');
      }
      handleCloseDialog();
      fetchData();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
        (editingKey ? 'Failed to update API key' : 'Failed to create API key');
      showError(errorMessage);
      console.error('API key submit error:', err);
    }
  };

  const handleDeleteClick = (keyId) => {
    setConfirmDialog({ open: true, keyId });
  };

  const handleDelete = async () => {
    try {
      await API.apiKeys.delete(confirmDialog.keyId);
      showSuccess('API key deleted successfully!');
      fetchData();
    } catch (err) {
      showError('Failed to delete API key');
      console.error('API key delete error:', err);
    }
  };

  const handleStatusToggle = async (apikeyid, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      await API.apiKeys.update(apikeyid, { status: newStatus });
      showSuccess(`API key ${newStatus === 1 ? 'activated' : 'deactivated'} successfully!`);
      fetchData();
    } catch (err) {
      showError('Failed to update API key status');
      console.error('API key status update error:', err);
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.project_id === projectId);
    return project ? project.project_name : 'Unknown';
  };

  const toggleShowKey = (keyId) => {
    setShowKey(prev => ({
      ...prev,
      [keyId]: !prev[keyId]
    }));
  };

  const maskApiKey = (key) => {
    if (!key) return '';
    return key.substring(0, 8) + '...' + key.substring(key.length - 4);
  };

  const filteredKeys = apiKeys.filter(key => {
    const matchesSearch = key.keyname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getProjectName(key.project_id)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      key.apikeyprefix?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || key.status === parseInt(statusFilter);
    const matchesProject = projectFilter === 'all' || key.project_id === projectFilter;
    
    return matchesSearch && matchesStatus && matchesProject;
  });

  const columns = [
    {
      field: 'keyname',
      headerName: 'Key Name',
      width: '20%',
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {row.keyname || 'Unnamed Key'}
        </Typography>
      ),
    },
    {
      field: 'apikeyprefix',
      headerName: 'Key Prefix',
      width: '15%',
      render: (row) => (
        <Chip 
          label={row.apikeyprefix} 
          size="small" 
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'project_id',
      headerName: 'Project',
      width: '20%',
      render: (row) => getProjectName(row.project_id),
    },
    {
      field: 'expiresat',
      headerName: 'Expires At',
      width: '12%',
      render: (row) => row.expiresat ? new Date(row.expiresat).toLocaleDateString() : 'Never',
    },
    {
      field: 'lastusedat',
      headerName: 'Last Used',
      width: '12%',
      render: (row) => row.lastusedat ? new Date(row.lastusedat).toLocaleDateString() : 'Never',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: '10%',
      sortable: false,
      render: (row) => (
        <Switch
          checked={row.status === 1}
          onChange={() => handleStatusToggle(row.apikeyid, row.status)}
          size="small"
          color="primary"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: '11%',
      render: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : '-',
    },
  ];

  const filterFields = [
    {
      label: 'Status',
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: 'all', label: 'All Status' },
        { value: '1', label: 'Active' },
        { value: '0', label: 'Inactive' },
      ],
    },
    {
      label: 'Project',
      value: projectFilter,
      onChange: setProjectFilter,
      options: [
        { value: 'all', label: 'All Projects' },
        ...projects.map(project => ({ value: project.project_id, label: project.project_name })),
      ],
    },
  ];

  if (loading) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            API Keys
          </Typography>
        </Box>
        <TableSkeleton columns={6} rows={6} />
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
            API Keys
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage API access keys and permissions
          </Typography>
        </Box>
        <ActionButton
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add API Key
        </ActionButton>
      </Box>

      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search API keys..."
        filterFields={filterFields}
      />

      <DataTable
        columns={columns}
        data={filteredKeys}
        onEdit={handleOpenDialog}
        onDelete={(row) => handleDeleteClick(row.apikeyid)}
        getRowId={(row) => row.apikeyid}
        emptyMessage="No API keys found"
      />

      <Dialog 
        open={open} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
        }}>
          <Typography variant="h6" fontWeight={700}>
            {editingKey ? 'Edit API Key' : 'Add New API Key'}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Key Name"
                value={formData.keyname}
                onChange={(e) => setFormData({ ...formData, keyname: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Project"
                value={formData.project_id}
                onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value="">Select Project</MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.project_id} value={project.project_id}>
                    {project.project_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expires At"
                type="date"
                value={formData.expiresat}
                onChange={(e) => setFormData({ ...formData, expiresat: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Leave empty for no expiration"
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: parseInt(e.target.value) })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <Box sx={{ px: 3, pb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <ActionButton variant="outlined" onClick={handleCloseDialog}>
            Cancel
          </ActionButton>
          <ActionButton variant="contained" onClick={handleSubmit}>
            {editingKey ? 'Update API Key' : 'Create API Key'}
          </ActionButton>
        </Box>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, keyId: null })}
        onConfirm={handleDelete}
        title="Delete API Key"
        message="Are you sure you want to delete this API key? This action cannot be undone and will immediately revoke access."
        confirmText="Delete"
        type="danger"
      />
    </MotionBox>
  );
}

export default APIKeys;